import { useEffect, useState } from "react";
import { Card, CardContent, Typography, Button, Divider, Box, TextField, Grid } from "@mui/material";
import { useElectronicsConfiguration } from "../../context/ElectronicsConfigurationContext.tsx";
import { useMechanicsConfiguration } from "../../context/MechanicsConfigurationContext.tsx";
import { useProject } from "../../context/ProjectContext.tsx";
import { useTestProcedure } from "../../context/TestProcedureContext.tsx";
import { PriceCalculationRequest } from "../../models/priceCalculationRequest.ts";
import { PriceResponse } from "../../models/priceResponse.ts";
import { useCalculatePrice } from "../../hooks/useCalculatePrice.ts";
import { useOffer } from "../../hooks/useOffer.ts";
import PageLayout from '../../components/PageLayout.tsx';
import PriceResponseView from "../../components/PriceResponseView.tsx"

const PricePage = () => {
  const { config: mechanicsConfig } = useMechanicsConfiguration();
  const { config: electronicsConfig } = useElectronicsConfiguration();
  const { project } = useProject();
  const { procedures } = useTestProcedure();
  const { calculatePrice: calc } = useCalculatePrice();
  const { requestOffer, statusMessage } = useOffer();
  const [calculation, setCalculation] = useState<PriceResponse>();
  const [numberOfSystems, setNumberOfSystems] = useState<number>(2);
  const [name, setName] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const fetchPrice = async () => {
      const request: PriceCalculationRequest = {
        mechanics: mechanicsConfig,
        electronics: electronicsConfig,
        number_of_systems: numberOfSystems,
      };
      const price = await calc(request);
      if (price !== null) {
        setCalculation(price);
      }
    };

    fetchPrice();
  }, [mechanicsConfig, electronicsConfig, numberOfSystems, calc]);

  const handleSubmit = async () => {
    await requestOffer({config:{project: project, mechanics: mechanicsConfig, electronics: electronicsConfig, test_procedures: procedures}, number_of_systems: numberOfSystems, name, company, email, phone, message})
    setName("");
    setEmail("");
    setCompany("");
    setPhone("");
    setMessage("");
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid = name.trim() !== "" && isValidEmail(email.trim()) && message.trim() !== "";

  return (
    <PageLayout
      title="Preis und Anfrage"
      backText="Zurück zur Zusammenfassung"
      backTarget="/summary"
    >
      <Card>
        <CardContent>
          <PriceResponseView 
            label="Preis"
            calculation={calculation} 
            numberOfSystems={numberOfSystems} 
            setNumberOfSystems={setNumberOfSystems} 
          />
        </CardContent>
      </Card>     
      <Card>
        <CardContent>
          <Typography variant="h6">Anfrage senden</Typography>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField fullWidth required label="Name" value={name} onChange={e => setName(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Firma" value={company} onChange={e => setCompany(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth required label="E-Mail" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Telefonnummer" value={phone} onChange={e => setPhone(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth required multiline rows={4} label="Nachricht" value={message} onChange={e => setMessage(e.target.value)} />
            </Grid>
          </Grid>

          <Box mt={3} display="flex" alignItems="center" gap={2}>
            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isFormValid}>
              Absenden
            </Button>
            {statusMessage && (
              <Typography color="primary" variant="subtitle1">{statusMessage}</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </PageLayout>
  );
};

export default PricePage;
